<template>
    <div class="w-full h-full">
        <transition
            enter-active-class="transition duration-400 ease-in"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-100 ease-out"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="h-full"
        >
            <div v-if="isLoading" class="fixed top-0 bottom-0 left-0 right-0 bg-black/[0.7] flex flex-col justify-center items-center z-40"
                    tabindex="-1" >
                <div class="flex flex-col justify-center items-center p-4 rounded-md">
                <svg aria-hidden="true" class="mx-1 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="#d3d3d3"/>
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="#2E9CCA"/>
                </svg>
                <div class="text-white p-4 whitespace-nowrap text-sm">
                    Please Wait While We Make Changes To {{ user.first_name }} {{ user.last_name }}.
                </div>
                </div>
            </div>
        </transition>
        <div class="pt-1 w-full mb-2">
            <BaseTabBar 
                :tabList="tabList"
                :selectedColor="selectedColor"
                :notSelectedColor="notSelectedColor"
                :hoverColor="hoverColor"
                @set-page="setView"
            />
        </div>
        <div v-if="isWorkflowVisible && canViewWorkflows" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <UserWorkflow
                :user="user"
                :person="person"
                :workflow-steps="workflowSteps"
                @refetch-users="fetchNewPeople"
                class="mt-3"

            />
        </div>
        <div v-if="isExportLimitsVisible && canEditPeopleUsersAndExportLimits" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <UserExportLimits
                :user="user"
                @refetch-users="fetchNewPeople"
                @close="close"
                class="mt-3"

            />
        </div>
        <div v-if="isEditUserFormVisible && canEditPeopleUsersAndExportLimits" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <UserForm
                :user="user"
                :groups="groups"
                @refetch-users="fetchNewPeople"
                @close="close"
                class="mt-3"

            />
        </div>
        <div v-if="isEditPersonFormVisible && canEditPeopleUsersAndExportLimits" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <PersonForm
                :person="person"
                @refetch-people="fetchNewPeople"
                @close="close"
                class="mt-3"

            />
        </div>
        <div v-if="isUploadedPicturesVisible && canViewWorkflows" class="px-2 py-1 overflow-y-scroll h-[95%]">
            <UserUploadedPictures
                :user="user"
                @close="close"
                class="mt-3"

            />
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue'
import BaseTabBar from '../BaseTabBar.vue';
import UserWorkflow from './UserWorkflow.vue';
import UserExportLimits from './UserExportLimits.vue';
import UserForm from './UserForm.vue';
import PersonForm from './PersonForm.vue';
import UserUploadedPictures from './UserUploadedPictures.vue';
import { useStore } from 'vuex';

const props = defineProps([
    "user", "addOrEdit", "defaultTab", "person", "workflowSteps", "groups"
])

const store = useStore()

const emit = defineEmits(["refetch-people", "close"]);

function fetchNewPeople(){
    emit('refetch-people')
}

function close(){
    emit("close")
}

const canViewWorkflows = computed(() => {
    let userPermissionsStore = store.getters['auth/showUserPermissions']
    return userPermissionsStore.isViewUserWorkflow || userPermissionsStore.isDev || userPermissionsStore.isSuperUser
})

const canEditPeopleUsersAndExportLimits = computed(() => {
    let userPermissionsStore = store.getters['auth/showUserPermissions']
    return userPermissionsStore.isManagement || userPermissionsStore.isDev || userPermissionsStore.isSuperUser
})

const isLoading = ref(false)

const tabList = computed(() => {
    // const tabs = [{tabName: 'Edit Client', isSelected: true}]
    const tabs = []
    if(canEditPeopleUsersAndExportLimits.value){
        tabs.push({tabName: 'Edit Person', isSelected: true} )
        tabs.push({tabName: 'Edit User', isSelected: false} )
        tabs.push({tabName: 'Export Limits', isSelected: false} )
    }
    if(canViewWorkflows.value){
        tabs.push({tabName: 'Uploaded Pictures', isSelected: false} )
        tabs.push({tabName: 'Workflow', isSelected: false} )
    }
    return tabs
}
)
const selectedColor = 'white';
const notSelectedColor = 'gray-100'
const hoverColor = 'slate-50'

const isEditPersonFormVisible = ref(false)
const isEditUserFormVisible = ref(false)
const isWorkflowVisible = ref(false)
const isExportLimitsVisible = ref(false)
const isUploadedPicturesVisible = ref(false)

function showFirstTab(){
    setView(tabList.value[0].tabName)
}


function setDefaultTab(){
    if(props.defaultTab){
        setView(props.defaultTab)
    } else {
        showFirstTab()
    }
}

setDefaultTab()

function showEditPersonForm(){

    isEditPersonFormVisible.value = true;
    isEditUserFormVisible.value = false;
    isWorkflowVisible.value = false;
    isExportLimitsVisible.value = false;
    isUploadedPicturesVisible.value = false;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Edit Person"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showEditUserForm(){
    isEditUserFormVisible.value = true;
    isEditPersonFormVisible.value = false;
    isWorkflowVisible.value = false;
    isExportLimitsVisible.value = false;
    isUploadedPicturesVisible.value = false;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Edit User"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showWorkflow(){
    isWorkflowVisible.value = true;
    isEditPersonFormVisible.value = false;
    isExportLimitsVisible.value = false;
    isEditUserFormVisible.value = false;
    isUploadedPicturesVisible.value = false;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Workflow"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showExportLimits(){
    isExportLimitsVisible.value = true;
    isEditPersonFormVisible.value = false;
    isWorkflowVisible.value = false;
    isEditUserFormVisible.value = false;
    isUploadedPicturesVisible.value = false;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Export Limits"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function showUploadedPictures(){
    isUploadedPicturesVisible.value = true;
    isExportLimitsVisible.value = false;
    isEditPersonFormVisible.value = false;
    isWorkflowVisible.value = false;
    isEditUserFormVisible.value = false;
    tabList.value.forEach((tab) => {
        if(tab.tabName === "Uploaded Pictures"){
            tab.isSelected = true
        } else {
            tab.isSelected = false
        }
    })
}

function setView(tabName) {
    if (tabName == 'Workflow') {
        showWorkflow();
    }
    if (tabName == 'Export Limits') {
        showExportLimits();
    }
    if (tabName == 'Edit User') {
        showEditUserForm();
    }
    if (tabName == 'Edit Person') {
        showEditPersonForm();
    }
    if (tabName == 'Uploaded Pictures') {
        showUploadedPictures();
    }
}



</script>